"use client";

import { ReactNode, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type MotionSectionProps = {
  className?: string;
  children: ReactNode;
  delay?: number;
};

export function MotionSection({
  className,
  children,
  delay = 0,
}: MotionSectionProps) {
  return (
    <motion.section
      initial={false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

type MotionCardProps = {
  className?: string;
  children: ReactNode;
  delay?: number;
};

export function MotionCard({
  className,
  children,
  delay = 0,
}: MotionCardProps) {
  return (
    <motion.div
      initial={false}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{
        y: -4,
        rotateX: 2,
        rotateY: -2,
        transition: { duration: 0.22 },
      }}
      className={className}
      style={{ transformStyle: "preserve-3d" }}
    >
      {children}
    </motion.div>
  );
}

type FaqItem = {
  q: string;
  a: string;
};

type AnimatedFaqListProps = {
  items: FaqItem[];
};

export function AnimatedFaqList({ items }: AnimatedFaqListProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <motion.article
            key={item.q}
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{
              duration: 0.35,
              delay: index * 0.05,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="overflow-hidden rounded-2xl border border-primaryLoc/15 bg-secondary/35 open:border-primaryLoc/35"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-center justify-between gap-4 p-5 text-start"
              aria-expanded={isOpen}
            >
              <span className="text-sm font-semibold text-primaryLoc">
                {item.q}
              </span>
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.25 }}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primaryLoc/25 text-primaryLoc/75"
              >
                v
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="px-5 pb-5 text-sm text-primaryLoc/75">
                    {item.a}
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.article>
        );
      })}
    </div>
  );
}
